import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import Btn from '../../../components/Buttons/Btn';
import AsyncTable, { Column } from '../../../components/AsyncTable';
import ModalViewJSON from '../../../components/ModalViewJSON';
import { frontendNotification } from '../../../components/Notification';
import { NotificationTypes } from '../../../components/Notification/notificationEnums';
import { ActionButtonsContainer } from '../../../components/TablePaginator/styles';
import { UrlPaths } from '../../../enums/urlPaths.enum';
import { DeleteIcon, PreviewIcon } from '../../../icons';
import history from '../../../services/history';
import { superContactsApi } from '../../../services/requests';
import { StoreState } from '../../../store/createStore';
import ContentContainer from '../../../templates/Content/ContentContainer';
import { Contact, ContactOtherInfo } from '../../../types/apiResponse/contact';
import getGenericRequestPageForAsyncTable from '../../../utils/TablesFunctions';
import AddContactModal from './components/AddContactModal';
import DeleteContactModal from './components/DeleteContactModal';

const ContactsPage = () => {
  const { user } = useSelector((state: StoreState) => state.auth);
  const { t } = useTranslation();
  const theme = useTheme();

  const [updateTable, setUpdateTable] = useState(0);
  const [contacts, setContacts] = useState<Contact[]>([]);

  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Contact | undefined>(undefined);
  const [dataToViewInJson, setDataToViewInJson] = useState<ContactOtherInfo | null>(null);

  useEffect(() => {
    if (!user?.is_superadmin) {
      frontendNotification({
        message: 'Área restrita!',
        type: NotificationTypes.WARNING,
      });

      history.push(UrlPaths.LOGIN);
    }
  }, [user]);

  const actionButtons = (row: Contact) => (
    <ActionButtonsContainer>
      <DeleteIcon
        title={t('delete')}
        onClick={() => {
          setSelectedItem(row);
          setShowDeleteModal(true);
        }}
      />
    </ActionButtonsContainer>
  );

  const handleOtherInfoColumn = (row: Contact) => {
    if (!row.other_info || Object.keys(row.other_info).length === 0) {
      return <span>-</span>;
    }

    return (
      <PreviewIcon
        title={t('toView')}
        style={{ width: '100%' }}
        onClick={() => setDataToViewInJson(row.other_info)}
      />
    );
  };

  const columns: Column<Contact>[] = [
    {
      label: t('tenant') as string,
      Cell: (row: Contact) => (
        <span>{row.tenant ? `${row.tenant_id} - ${row.tenant.name}` : row.tenant_id}</span>
      ),
    },
    {
      label: t('contact') as string,
      accessor: 'contact',
    },
    {
      label: t('sector') as string,
      accessor: 'sector',
    },
    {
      label: t('email') as string,
      Cell: (row: Contact) => <span>{row.email || '-'}</span>,
    },
    {
      label: t('phone') as string,
      Cell: (row: Contact) => <span>{row.phone || '-'}</span>,
    },
    {
      label: t('otherInfo') as string,
      Cell: handleOtherInfoColumn,
      headerCellStyle: { textAlign: 'center' },
      cellStyle: { textAlign: 'center' },
    },
    {
      Cell: actionButtons,
      headerCellStyle: { textAlign: 'center' },
      cellStyle: { textAlign: 'center' },
    },
  ];

  return (
    <ContentContainer title={t('contacts')}>
      {dataToViewInJson && (
        <ModalViewJSON
          showModal={Boolean(dataToViewInJson)}
          closeModal={() => setDataToViewInJson(null)}
          data={dataToViewInJson}
          title={t('otherInfo')}
        />
      )}

      <Btn
        text={t('register')}
        onClick={() => setShowAddContactModal(true)}
        style={{ marginLeft: '0', marginBottom: '1rem' }}
      />

      {showAddContactModal && (
        <AddContactModal
          showModal={showAddContactModal}
          closeModal={() => setShowAddContactModal(false)}
          callback={() => setUpdateTable(updateTable + 1)}
        />
      )}

      {showDeleteModal && selectedItem && (
        <DeleteContactModal
          showModal={showDeleteModal}
          selectedItem={selectedItem}
          closeModal={() => {
            setShowDeleteModal(false);
            setSelectedItem(undefined);
          }}
          callback={() => setUpdateTable(updateTable + 1)}
        />
      )}

      <AsyncTable
        tableName={t('contacts')}
        columns={columns}
        value={contacts}
        onChange={setContacts}
        requestPage={getGenericRequestPageForAsyncTable(superContactsApi)}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />
    </ContentContainer>
  );
};

export default ContactsPage;
